import type {
    MediaResponse,
    PaginatedMediaResponse,
    UploadMediaRequest,
    UpdateAttachmentRequest,
    DeleteMediaResponse,
    MediaFilters,
    CropMediaRequest,
    CropMediaResponse,
    MediaFolder,
    CreateFolderRequest,
    MoveMediaRequest
} from '@/types/media';
import apiClient from './meta-client';

const META_API_ORIGIN = (import.meta.env.VITE_META_API_URL || 'http://localhost:8000/api/v1')
    .replace(/\/api\/v\d+.*$/, '');

const absolutizar = (url?: string | null): string | undefined => {
    if (!url) return undefined;
    if (url.startsWith('http')) return url;
    // URL sem domínio ex: "/images/foo.png" → falta também o /uploads
    const path = url.startsWith('/uploads') ? url : `/uploads${url}`;
    return `${META_API_ORIGIN}${path}`;
};

// Normaliza as três: a grade usa thumbnail_url e as telas usam preview_url —
// deixar essas duas sem domínio faria o front cair no arquivo original, que é
// justamente o que pesa (~1,5 MB por arte).
const normalizeFileUrl = (media: MediaResponse): MediaResponse => ({
    ...media,
    file_url: absolutizar(media.file_url) ?? media.file_url,
    thumbnail_url: absolutizar(media.thumbnail_url) ?? media.thumbnail_url,
    preview_url: absolutizar(media.preview_url) ?? media.preview_url,
});

// ========== MEDIA API ==========

/**
 * Listar todas as mídias do usuário
 * @param filters - Filtros opcionais (media_type, page_id, page, per_page)
 * @returns Lista de mídias
 */
export const getMedias = async (filters?: MediaFilters): Promise<MediaResponse[]> => {
    const params: Record<string, string> = {};

    if (filters?.media_type) {
        params.media_type = filters.media_type;
    }

    if (filters?.page_id) {
        params.page_id = filters.page_id;
    }

    if (filters?.page) {
        params.page = String(filters.page);
    }

    if (filters?.per_page) {
        params.per_page = String(filters.per_page);
    }

    if (filters?.folder_id !== undefined) {
        params.folder_id = String(filters.folder_id);
    }

    if (filters?.busca) {
        params.busca = filters.busca;
    }

    const queryString = new URLSearchParams(params).toString();

    const response = await apiClient.get<PaginatedMediaResponse>(
        `/media${queryString ? `?${queryString}` : ''}`
    );

    // Retorna apenas os items do objeto paginado
    return response.data.items.map(normalizeFileUrl);
};

/**
 * Listar mídias com informações de paginação
 * @param filters - Filtros opcionais (media_type, page_id, page, per_page)
 * @returns Resposta paginada completa
 */
export const getMediasPaginated = async (filters?: MediaFilters): Promise<PaginatedMediaResponse> => {
    const params: Record<string, string> = {};

    if (filters?.media_type) {
        params.media_type = filters.media_type;
    }

    if (filters?.page_id) {
        params.page_id = filters.page_id;
    }

    if (filters?.page) {
        params.page = String(filters.page);
    }

    if (filters?.per_page) {
        params.per_page = String(filters.per_page);
    }

    if (filters?.folder_id !== undefined) {
        params.folder_id = String(filters.folder_id);
    }

    if (filters?.busca) {
        params.busca = filters.busca;
    }

    const queryString = new URLSearchParams(params).toString();

    const response = await apiClient.get<PaginatedMediaResponse>(
        `/media${queryString ? `?${queryString}` : ''}`
    );

    return {
        ...response.data,
        items: response.data.items.map(normalizeFileUrl),
    };
};

/**
 * Obter mídia específica por ID
 * @param mediaId - ID da mídia
 * @returns Dados da mídia
 */
export const getMedia = async (mediaId: number): Promise<MediaResponse> => {
    const response = await apiClient.get<MediaResponse>(`/media/${mediaId}`);
    return normalizeFileUrl(response.data);
};

/**
 * Upload de nova mídia
 * @param data - Dados do upload (file, page_id, send_to_meta)
 * @returns Mídia criada
 */
export const uploadMedia = async (data: UploadMediaRequest): Promise<MediaResponse> => {
    const formData = new FormData();
    formData.append('file', data.file);
    formData.append('page_id', data.page_id);

    // Se não especificado, envia para Meta apenas se for vídeo ou áudio
    const sendToMeta = data.send_to_meta ?? (
        data.file.type.startsWith('video/') || data.file.type.startsWith('audio/')
    );
    formData.append('send_to_meta', String(sendToMeta));

    if (data.folder_id !== undefined) {
        formData.append('folder_id', String(data.folder_id));
    }

    const response = await apiClient.post<MediaResponse>('/media/upload', formData, {
        headers: {
            'Content-Type': 'multipart/form-data'
        }
    });
    return normalizeFileUrl(response.data);
};

/**
 * Atualizar attachment_id de uma mídia (fallback manual)
 * @param mediaId - ID da mídia
 * @param data - Novo attachment_id
 * @returns Mídia atualizada
 */
export const updateMediaAttachment = async (
    mediaId: number,
    data: UpdateAttachmentRequest
): Promise<MediaResponse> => {
    const response = await apiClient.put<MediaResponse>(
        `/media/${mediaId}/attachment`,
        data
    );
    return response.data;
};

/**
 * Deletar mídia
 * @param mediaId - ID da mídia
 * @returns Resposta de sucesso
 */
export const deleteMedia = async (mediaId: number): Promise<DeleteMediaResponse> => {
    const response = await apiClient.delete<DeleteMediaResponse>(`/media/${mediaId}`);
    return response.data;
};

// ========== HELPER FUNCTIONS ==========

/**
 * Valida tipo de arquivo baseado no mediaType
 * @param file - Arquivo a validar
 * @param mediaType - Tipo de mídia esperado
 * @returns true se válido
 */
export const validateMediaType = (
    file: File,
    mediaType: 'image' | 'video' | 'audio' | 'all'
): boolean => {
    const validTypes: Record<string, string[]> = {
        image: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
        video: ['video/mp4', 'video/quicktime'],
        audio: ['audio/mpeg', 'audio/mp3', 'audio/ogg', 'audio/wav'],
        all: [
            'image/jpeg', 'image/png', 'image/gif', 'image/webp',
            'video/mp4', 'video/quicktime',
            'audio/mpeg', 'audio/mp3', 'audio/ogg', 'audio/wav'
        ]
    };

    return validTypes[mediaType].includes(file.type);
};

/**
 * Valida tamanho do arquivo
 * @param file - Arquivo a validar
 * @param maxSizeMB - Tamanho máximo em MB (padrão: 25MB)
 * @returns true se válido
 */
export const validateFileSize = (file: File, maxSizeMB: number = 25): boolean => {
    const maxBytes = maxSizeMB * 1024 * 1024;
    return file.size <= maxBytes;
};

/**
 * Formata tamanho de arquivo para exibição
 * @param bytes - Tamanho em bytes
 * @returns String formatada (ex: "2.5 MB")
 */
export const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

/**
 * Obtém ícone/label baseado no tipo de mídia
 * @param mediaType - Tipo da mídia
 * @returns Label formatada
 */
export const getMediaTypeLabel = (mediaType: string): string => {
    const labels: Record<string, string> = {
        image: 'Imagem',
        video: 'Vídeo',
        audio: 'Áudio'
    };
    return labels[mediaType] || mediaType;
};

/**
 * Recorta uma imagem já existente na biblioteca para a proporção 1,91:1.
 *
 * O recorte roda no servidor (Pillow) e não no canvas do navegador: as imagens
 * são servidas de outro domínio, o que deixaria o canvas tainted e faria o
 * export falhar. O front manda só as coordenadas, em pixels do original.
 *
 * @param data - source_url + x/y/width/height no espaço da imagem original
 * @returns Imagem derivada (o original continua intacto na biblioteca)
 */
export const cropMedia = async (data: CropMediaRequest): Promise<CropMediaResponse> => {
    const response = await apiClient.post<CropMediaResponse>('/media/crop', data);
    if (!response.data.file_url) return response.data;
    const url = response.data.file_url.startsWith('http')
        ? response.data.file_url
        : `${META_API_ORIGIN}${response.data.file_url.startsWith('/uploads')
            ? response.data.file_url
            : `/uploads${response.data.file_url}`}`;
    return { ...response.data, file_url: url };
};


// ========== PASTAS DA BIBLIOTECA ==========

/** Lista as pastas do usuário (lista plana; a árvore é montada no front) */
export const getFolders = async (): Promise<MediaFolder[]> => {
    const response = await apiClient.get<MediaFolder[]>('/media/folders');
    return response.data;
};

/** Cria uma pasta. parent_id 0 (ou omitido) cria na raiz. */
export const createFolder = async (data: CreateFolderRequest): Promise<MediaFolder> => {
    const response = await apiClient.post<MediaFolder>('/media/folders', {
        nome: data.nome,
        parent_id: data.parent_id ?? 0,
    });
    return response.data;
};

/** Renomeia uma pasta */
export const renameFolder = async (folderId: number, nome: string): Promise<MediaFolder> => {
    const response = await apiClient.put<MediaFolder>(`/media/folders/${folderId}`, { nome });
    return response.data;
};

/** Move uma pasta para dentro de outra (parentId 0 = raiz) */
export const moveFolder = async (folderId: number, parentId: number): Promise<MediaFolder> => {
    const response = await apiClient.put<MediaFolder>(`/media/folders/${folderId}/move`, {
        parent_id: parentId,
    });
    return response.data;
};

/**
 * Exclui uma pasta. Nenhuma imagem é apagada — as mídias e subpastas sobem
 * para a pasta de cima.
 */
export const deleteFolder = async (
    folderId: number
): Promise<{ midias_movidas: number; subpastas_movidas: number }> => {
    const response = await apiClient.delete(`/media/folders/${folderId}`);
    return response.data;
};

/** Move mídias já existentes para uma pasta (0 = raiz) */
export const moveMedia = async (data: MoveMediaRequest): Promise<{ movidas: number }> => {
    const response = await apiClient.put('/media/move', data);
    return response.data;
};


export interface MediaEmUso {
    id: number;
    filename: string;
    original_filename?: string;
    usos: { nodes?: number; broadcasts?: number; templates?: number };
}

export interface DeleteManyResponse {
    sucesso?: boolean;
    apagadas?: number;
    // Quando vem `bloqueado`, NADA foi apagado: alguma arte está em uso e a
    // lista diz onde. Repetir com forcar=true exclui assim mesmo.
    bloqueado?: boolean;
    em_uso?: MediaEmUso[];
}

/**
 * Exclui várias mídias (banco + disco).
 *
 * Por padrão não apaga nada se alguma estiver em uso em node ou em broadcast
 * ainda não enviado — devolve a lista para o usuário decidir.
 */
export const deleteManyMedia = async (
    mediaIds: number[],
    forcar = false
): Promise<DeleteManyResponse> => {
    const response = await apiClient.post<DeleteManyResponse>('/media/delete', {
        media_ids: mediaIds,
        forcar,
    });
    return response.data;
};
